import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { environment } from '@/environments/environment';
import { Theming } from './theming';

/**
 * Widened on purpose. Each environment file declares `envName` with `as const`,
 * so TypeScript sees only the literal of whichever file is compiled in — and
 * the build that runs the unit tests compiles the DEV one. Comparing `'dev'`
 * to `'production'` is then "impossible" and errors as TS2367, even though
 * `fileReplacements` swaps a different file in for the real builds. At runtime
 * this is whichever of local | dev | staging | production was built.
 */
const envName: string = environment.envName;

/**
 * DevThemeToggle — a floating light/dark switch, on every screen, for the
 * NON-PRODUCTION builds only.
 *
 * The auth gateway is entirely unauthenticated surface (sign-in, register,
 * forgot/reset password, action handler), so mounting this beside the router
 * outlet in the root component is the only placement that covers all of it.
 *
 * It drives {@link Theming.scheme} — the app's real, locally-persisted scheme
 * preference — rather than a parallel preview state.
 *
 * PRODUCTION NEVER RENDERS IT. The gate is `envName`, deliberately NOT
 * `environment.production`: this app ships STAGING with `production: true` (it
 * is an AOT/optimisation flag), so that boolean does not mean "live site" here.
 */
@Component({
  selector: 'ce-dev-theme-toggle',
  imports: [MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (enabled) {
      <button
        type="button"
        (click)="toggle()"
        [attr.aria-label]="
          theming.isDark() ? 'Switch to light theme' : 'Switch to dark theme'
        "
        [title]="
          theming.isDark() ? 'Switch to light theme' : 'Switch to dark theme'
        "
        class="fixed bottom-4 left-4 z-[9999] flex items-center gap-1.5 rounded-full bg-neutral-900/90 px-3 py-1.5 text-white shadow-lg ring-1 ring-white/20 transition hover:bg-neutral-900 dark:bg-white/90 dark:text-neutral-900 dark:ring-black/20 dark:hover:bg-white"
      >
        <mat-icon
          [svgIcon]="theming.isDark() ? 'sun' : 'moon'"
          class="size-4 shrink-0"
        />
        <span class="text-[10px] font-extrabold tracking-wide">{{
          envLabel
        }}</span>
      </button>
    }
  `,
})
export class DevThemeToggle {
  protected readonly theming = inject(Theming);

  protected readonly enabled = envName !== 'production';

  /** Doubles as a "which build am I on?" badge. */
  protected readonly envLabel = envName.toUpperCase();

  protected toggle(): void {
    // Resolve against isDark() rather than scheme(), so a flip while on
    // 'system' lands on the explicit opposite of what is actually rendered.
    this.theming.scheme.set(this.theming.isDark() ? 'light' : 'dark');
  }
}
